<template>
  <div class="sv">
    <div class="svbox">
      <div class="svhead">
        <img src="@/assets/logo-wordmark-green.png" alt="CHDS 吃荤搭素" class="svlogo" />
        <button class="svlang" @click="switchLang">{{ lang === 'zh' ? 'English' : '中文' }}</button>
      </div>

      <div v-if="loading" class="svstate">…</div>
      <div v-else-if="loadError" class="svstate err">{{ loadError }}</div>

      <!-- DONE -->
      <div v-else-if="done" class="svdone">
        <div class="svtick">💚</div>
        <p v-for="(p, i) in outroLines" :key="i">{{ p }}</p>
      </div>

      <!-- ALREADY ANSWERED -->
      <div v-else-if="schema.already_completed" class="svdone">
        <div class="svtick">💚</div>
        <p>{{ lang === 'zh' ? '您已经完成这份问卷了，谢谢您！' : "You've already completed this survey — thank you!" }}</p>
      </div>

      <!-- INTRO -->
      <div v-else-if="!started" class="svintro">
        <p v-for="(p, i) in introLines" :key="i">{{ p }}</p>
        <button class="svbtn go" @click="begin">
          {{ lang === 'zh' ? '开始（约 3–5 分钟）' : 'Start (about 3–5 minutes)' }}
        </button>
      </div>

      <!-- QUESTIONS -->
      <form v-else class="svform" @submit.prevent="submit">
        <div class="svprog"><div class="svbar" :style="{ width: progress + '%' }"></div></div>

        <template v-for="sec in schema.sections" :key="sec.key">
          <div v-if="sectionVisible(sec.key)" class="svsec">
            <h2 class="svsect">{{ sec.title }}</h2>

            <div v-for="q in questionsIn(sec.key)" :key="q.id" class="svq" :class="{ bad: errors[q.id] }">
              <label class="svprompt">
                {{ q.prompt }}
                <span v-if="q.required" class="svreq">*</span>
              </label>

              <!-- MULTI -->
              <div v-if="q.type === 'multi'" class="svopts">
                <label v-for="o in optionsFor(q)" :key="o.code" class="svchk"
                       :class="{ on: (answers[q.id] || []).includes(o.code), off: atMax(q, o) }">
                  <input type="checkbox" :value="o.code" :checked="(answers[q.id] || []).includes(o.code)"
                         :disabled="atMax(q, o)" @change="toggleMulti(q, o.code)" />
                  <span>{{ o.label }}</span>
                </label>
                <div v-if="q.max" class="svhint">
                  {{ lang === 'zh' ? `最多选择 ${q.max} 项` : `Select up to ${q.max}` }}
                  · {{ (answers[q.id] || []).length }}/{{ q.max }}
                </div>
              </div>

              <!-- SINGLE -->
              <div v-else-if="q.type === 'single'" class="svopts">
                <label v-for="o in optionsFor(q)" :key="o.code" class="svchk"
                       :class="{ on: answers[q.id] === o.code }">
                  <input type="radio" :name="q.id" :value="o.code" v-model="answers[q.id]" />
                  <span>{{ o.label }}</span>
                </label>
              </div>

              <!-- MATRIX -->
              <div v-else-if="q.type === 'matrix'" class="svmatrix">
                <div v-for="r in q.rows" :key="r.code" class="svrow">
                  <span class="svrowlbl">{{ r.label }}</span>
                  <span class="svscale">
                    <button v-for="n in q.scale" :key="n" type="button" class="svdot"
                            :class="{ on: (answers[q.id] || {})[r.code] === n }"
                            :title="q.scale_labels ? q.scale_labels[n - 1] : String(n)"
                            @click="setMatrix(q, r.code, n)">{{ n }}</button>
                  </span>
                </div>
                <div class="svhint">1 = {{ q.scale_labels ? q.scale_labels[0] : '' }} · {{ q.scale }} = {{ q.scale_labels ? q.scale_labels[q.scale - 1] : '' }}</div>
              </div>

              <!-- 0–10 SCALE -->
              <div v-else-if="q.type === 'scale'" class="svnps">
                <button v-for="n in 11" :key="n - 1" type="button" class="svnum"
                        :class="{ on: answers[q.id] === n - 1 }" @click="answers[q.id] = n - 1">{{ n - 1 }}</button>
              </div>

              <!-- TEXT -->
              <input v-else-if="q.type === 'text'" class="svtext" v-model="answers[q.id]" />
              <textarea v-else class="svtext area" rows="4" v-model="answers[q.id]"></textarea>

              <!-- "Other" free text -->
              <input v-if="showsOther(q)" class="svtext other" v-model="answers[q.id + '__OTHER_TEXT']"
                     :placeholder="lang === 'zh' ? '请告诉我们…' : 'Please tell us…'" />

              <div v-if="errors[q.id]" class="sverr">{{ errors[q.id] }}</div>
            </div>
          </div>
        </template>

        <div v-if="schema.universal" class="svq svwho">
          <label class="svprompt">
            {{ lang === 'zh' ? '最后，方便留下您的联系方式吗？（选填）' : 'Last thing — may we have your contact details? (optional)' }}
          </label>
          <div class="svhint" style="margin-bottom:8px">
            {{ lang === 'zh'
              ? '这样我们才知道是谁的意见，如果需要也可以回复您。不填也可以提交。'
              : "So we know whose feedback this is, and can reply if you'd like. You can submit without it." }}
          </div>
          <input class="svtext" v-model="respondent.name"
                 :placeholder="lang === 'zh' ? '姓名' : 'Name'" />
          <input class="svtext" style="margin-top:8px" v-model="respondent.phone"
                 :placeholder="lang === 'zh' ? '电话' : 'Phone'" />
          <input class="svtext" style="margin-top:8px" v-model="respondent.email"
                 :placeholder="lang === 'zh' ? '邮箱' : 'Email'" />
        </div>

        <div v-if="submitError" class="sverr big">{{ submitError }}</div>
        <button class="svbtn go" type="submit" :disabled="saving">
          {{ saving ? (lang === 'zh' ? '提交中…' : 'Sending…') : (lang === 'zh' ? '提交问卷' : 'Submit') }}
        </button>
        <div class="svfoot">{{ lang === 'zh' ? '吃荤搭素，好好吃饭。💚' : 'CHDS — 好好吃饭. Eat Properly. 💚' }}</div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from '../../axios';

export default {
  name: 'SurveyPage',
  data() {
    return {
      token: this.$route.params.token,
      lang: 'en', schema: { sections: [], questions: [] },
      answers: {}, errors: {}, started: false, done: false,
      respondent: { name: '', phone: '', email: '' },
      loading: true, loadError: '', saving: false, submitError: '', outro: '',
    };
  },
  computed: {
    introLines() { return (this.schema.intro || '').split('\n').filter(Boolean); },
    outroLines() { return (this.outro || this.schema.outro || '').split('\n').filter(Boolean); },
    visibleQuestions() { return (this.schema.questions || []).filter((q) => this.visible(q)); },
    progress() {
      const qs = this.visibleQuestions;
      if (!qs.length) return 0;
      const done = qs.filter((q) => this.hasAnswer(q)).length;
      return Math.round((done / qs.length) * 100);
    },
  },
  created() {
    // Follow the site's language if the customer already picked one.
    try {
      const l = localStorage.getItem('chds_lang') || localStorage.getItem('lang') || navigator.language || '';
      if (String(l).toLowerCase().startsWith('zh')) this.lang = 'zh';
    } catch (e) { /* noop */ }
    this.load();
  },
  methods: {
    async load() {
      this.loading = true; this.loadError = '';
      try {
        const { data } = await axios.get(`/survey/${this.token}/`, { params: { lang: this.lang } });
        this.schema = data;
      } catch (e) {
        this.loadError = this.lang === 'zh'
          ? '这个问卷链接无效或已过期。' : "This survey link isn't valid or has expired.";
      } finally { this.loading = false; }
    },
    async switchLang() {
      this.lang = this.lang === 'zh' ? 'en' : 'zh';
      await this.load();          // same codes, different display (one survey)
    },
    async begin() {
      this.started = true;
      try { await axios.post(`/survey/${this.token}/start/`, {}); } catch (e) { /* tracking only */ }
    },
    questionsIn(sec) { return this.visibleQuestions.filter((q) => q.section === sec); },
    sectionVisible(sec) { return this.questionsIn(sec).length > 0; },

    /** Q03's options are whatever they picked in Q02 (brief: "ONLY display options selected"). */
    optionsFor(q) {
      if (!q.options_from) return q.options;
      const src = (this.schema.questions || []).find((x) => x.id === q.options_from);
      const picked = this.answers[q.options_from] || [];
      return (src ? src.options : []).filter((o) => picked.includes(o.code));
    },
    visible(q) {
      const c = q.show_if;
      if (!c) return true;
      const v = this.answers[c.question];
      if (c.answered) return !!(v && String(v).trim());
      if (c.in) return c.in.includes(v);
      if (c.any) return (v || []).some((x) => c.any.includes(x));
      return true;
    },
    hasAnswer(q) {
      const v = this.answers[q.id];
      if (q.type === 'multi') return (v || []).length > 0;
      if (q.type === 'matrix') return Object.keys(v || {}).length >= (q.rows || []).length;
      if (q.type === 'scale') return typeof v === 'number';
      return !!(v && String(v).trim());
    },
    atMax(q, o) {
      const cur = this.answers[q.id] || [];
      return !!q.max && cur.length >= q.max && !cur.includes(o.code);
    },
    toggleMulti(q, code) {
      const cur = [...(this.answers[q.id] || [])];
      const i = cur.indexOf(code);
      if (i >= 0) cur.splice(i, 1);
      else if (!q.max || cur.length < q.max) cur.push(code);
      this.answers[q.id] = cur;
      // If they de-select a reason, a primary reason pointing at it is no longer valid.
      const prim = this.answers.Q03_PRIMARY_CHURN_REASON;
      if (prim && q.id === 'Q02_CHURN_REASONS' && !cur.includes(prim)) {
        this.answers.Q03_PRIMARY_CHURN_REASON = '';
      }
    },
    setMatrix(q, row, n) { this.answers[q.id] = { ...(this.answers[q.id] || {}), [row]: n }; },
    showsOther(q) {
      if (!q.other_option) return false;
      const v = this.answers[q.id];
      return Array.isArray(v) ? v.includes(q.other_option) : v === q.other_option;
    },
    validate() {
      const errs = {};
      const need = this.lang === 'zh' ? '请回答这一题' : 'Please answer this question';
      this.visibleQuestions.forEach((q) => { if (q.required && !this.hasAnswer(q)) errs[q.id] = need; });
      this.errors = errs;
      return Object.keys(errs).length === 0;
    },
    async submit() {
      this.submitError = '';
      if (!this.validate()) {
        this.submitError = this.lang === 'zh'
          ? '还有几题需要回答（已用红色标出）。' : 'A few answers are still needed — highlighted below.';
        const el = document.querySelector('.svq.bad');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      this.saving = true;
      try {
        const { data } = await axios.post(`/survey/${this.token}/submit/`,
          { language: this.lang, answers: this.answers, respondent: this.respondent });
        this.outro = data.outro || '';
        this.done = true;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        this.submitError = e.response?.data?.detail
          || (this.lang === 'zh' ? '提交失败，请再试一次。' : "That didn't send — please try again.");
      } finally { this.saving = false; }
    },
  },
};
</script>

<style scoped>
.sv { min-height: 100vh; background: #f4f6f0; padding: 22px 14px 60px; }
.svbox { max-width: 720px; margin: 0 auto; background: #fff; border-radius: 16px; padding: 26px 24px 30px; box-shadow: 0 6px 26px rgba(31,36,33,.07); }
.svhead { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.svlogo { height: 34px; }
.svlang { border: 1px solid #dfe1da; background: #fff; border-radius: 999px; padding: 5px 14px; font-size: 12.5px; cursor: pointer; color: #446936; }
.svstate { padding: 40px 0; text-align: center; color: #8a908a; }
.svstate.err { color: #a32d2d; }
.svintro p, .svdone p { font-size: 14.5px; line-height: 1.75; color: #3b423c; margin: 0 0 10px; white-space: pre-line; }
.svtick { font-size: 44px; text-align: center; margin-bottom: 10px; }
.svdone { text-align: center; padding: 20px 0; }
.svbtn.go { display: block; width: 100%; margin-top: 18px; background: #446936; color: #fff; border: none; border-radius: 12px; padding: 14px; font-size: 15px; font-weight: 700; cursor: pointer; }
.svbtn.go:disabled { opacity: .6; }
.svprog { height: 5px; background: #eef1e8; border-radius: 999px; overflow: hidden; margin-bottom: 20px; position: sticky; top: 0; }
.svbar { height: 100%; background: #B6D452; transition: width .25s; }
.svsect { font-size: 13px; letter-spacing: .04em; text-transform: uppercase; color: #7d8a6b; margin: 26px 0 12px; font-weight: 700; }
.svq { padding: 14px 0 4px; border-top: 1px solid #f1f3ec; }
.svq.bad { background: #fff8f8; border-radius: 10px; padding: 12px; }
.svprompt { display: block; font-size: 14.5px; font-weight: 600; color: #2b2f2a; line-height: 1.55; margin-bottom: 10px; }
.svreq { color: #c0526a; margin-left: 3px; }
.svopts { display: flex; flex-direction: column; gap: 7px; }
.svchk { display: flex; align-items: flex-start; gap: 9px; border: 1px solid #e2e5dc; border-radius: 10px; padding: 10px 12px; font-size: 14px; color: #3b423c; cursor: pointer; line-height: 1.45; }
.svchk.on { border-color: #446936; background: #f3f8ea; color: #27500a; font-weight: 600; }
.svchk.off { opacity: .45; cursor: not-allowed; }
.svchk input { margin-top: 3px; flex: none; accent-color: #446936; }
.svhint { font-size: 11.5px; color: #9aa093; margin-top: 4px; }
.svmatrix { display: flex; flex-direction: column; gap: 8px; }
.svrow { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.svrowlbl { font-size: 13.5px; color: #3b423c; flex: 1 1 150px; }
.svscale { display: flex; gap: 5px; }
.svdot { width: 34px; height: 34px; border-radius: 8px; border: 1px solid #dfe1da; background: #fff; cursor: pointer; font-size: 13px; color: #5a615b; }
.svdot.on { background: #446936; color: #fff; border-color: #446936; font-weight: 700; }
.svnps { display: flex; gap: 5px; flex-wrap: wrap; }
.svnum { width: 40px; height: 40px; border-radius: 9px; border: 1px solid #dfe1da; background: #fff; cursor: pointer; font-size: 13.5px; color: #5a615b; }
.svnum.on { background: #446936; color: #fff; border-color: #446936; font-weight: 700; }
.svtext { width: 100%; border: 1px solid #dfe1da; border-radius: 10px; padding: 11px 12px; font-size: 14px; font-family: inherit; box-sizing: border-box; }
.svtext.area { resize: vertical; line-height: 1.6; }
.svtext.other { margin-top: 8px; }
.sverr { color: #a32d2d; font-size: 12.5px; margin-top: 7px; }
.sverr.big { margin-top: 16px; text-align: center; }
.svwho { background: #f7faf2; border-radius: 10px; padding: 14px; border-top: none; margin-top: 18px; }
.svfoot { text-align: center; font-size: 12.5px; color: #8a908a; margin-top: 18px; }
@media (max-width: 560px) {
  .svbox { padding: 18px 15px 24px; border-radius: 12px; }
  .svnum { width: 30px; height: 36px; }
  .svdot { width: 30px; height: 32px; }
  .svtext, .svchk { font-size: 16px; }   /* 16px stops iOS zooming the page on focus */
}
</style>
